import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MdDialog } from '@angular/material';
import { DateAdapter, MdSnackBar } from '@angular/material';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { Observable } from 'rxjs/observable';
import { Subscription } from 'rxjs/Subscription';

import { Ad } from '../../enums/ad.enum';
import { Interest } from '../../enums/interest.enum';
import { EnumHelper } from '../../helpers/enum-helper';
import { FormHelper } from '../../helpers/form-helper';
import { Application } from '../../models/application';
import { Payment } from '../../models/payment';
import { Meetup } from '../../models/meetup';
import { ApplicationService } from '../../services/application.service';
import { AttendeeService } from '../../services/attendee.service';
import { MeetupService } from '../../services/meetup.service';
import { RemovePaymentComponent } from '../remove-payment/remove-payment.component';

@Component({
	selector: 'app-update-application',
	templateUrl: './update-application.component.html',
	styleUrls: ['./update-application.component.scss']
})
export class UpdateApplicationComponent extends FormHelper implements OnInit, OnDestroy{
	private subscriptionManager: Subscription;

	public meetups: Observable<Meetup[]>;
	public enumHelper: typeof EnumHelper = EnumHelper;
	public ads: typeof Ad = Ad;
	public adValues: number[] = Object.values(Ad).filter(e => typeof (e) == "number");
	public interests: typeof Interest = Interest;
	public interestValues: number[] = Object.values(Interest).filter(e => typeof (e) == "number");

	private key: string;
	public application: Observable<Application>;
	public payments: Observable<Payment[]>;

	constructor(private route: ActivatedRoute,
		private dialog: MdDialog,
		private applicationService: ApplicationService,
		private attendeeService: AttendeeService,
		private formBuilder: FormBuilder,
		private meetupService: MeetupService,
		private router: Router,
		private snackBar: MdSnackBar,
		private dateAdapter: DateAdapter<Date>) {
		super();
		this.subscriptionManager = new Subscription();
		this.dateAdapter.setLocale('en-US');

		this.buildForm();

		this.meetups = meetupService.getNextMeetups();
	}

	ngOnInit() {
		this.subscriptionManager.add(this.route
			.params
			.subscribe(params => {
				if (params['key']) {
					this.application = this.applicationService.getApplication(params['key']);

					this.subscriptionManager.add(this.application.subscribe(app => {
						this.key = app.$key;
						this.updateForm(app);
						this.payments = this.applicationService.getPayments(app.$key);
					}));
				}
			}));
	}

	ngOnDestroy() {
		this.subscriptionManager.unsubscribe();
	}

	public buildForm(): void {
		this.form = this.formBuilder.group({
			meetup: new FormControl('', Validators.required),
			he: new FormGroup({
				name: new FormControl('', Validators.required),
				lastname: new FormControl('', Validators.required),
				nickname: new FormControl('', Validators.required),
				phone: new FormControl('', Validators.required),
				email: new FormControl('', [Validators.required, Validators.email]),
				occupation: new FormControl('', Validators.required),
				education: new FormControl('', Validators.required)
			}),
			she: new FormGroup({
				name: new FormControl('', Validators.required),
				lastname: new FormControl('', Validators.required),
				nickname: new FormControl('', Validators.required),
				phone: new FormControl('', Validators.required),
				email: new FormControl('', [Validators.required, Validators.email]),
				occupation: new FormControl('', Validators.required),
				education: new FormControl('', Validators.required)
			}),
			address: new FormGroup({
				street: new FormControl('', Validators.required),
				exterior: new FormControl('', Validators.required),
				interior: new FormControl(),
				neighborhood: new FormControl('', Validators.required),
				zip: new FormControl('', Validators.required),
				city: new FormControl('', Validators.required),
				state: new FormControl('', Validators.required)
			}),
			couple: new FormGroup({
				marriage: new FormControl(),
				marriageYears: new FormControl(),
				catholicDate: new FormControl(),
				legalDate: new FormControl(),
				childrenNumber: new FormControl(),
				childrenNames: new FormControl(),
				childrenAges: new FormControl(),
				babysitter: new FormControl(),
				address: new FormControl(),
				phone: new FormControl()
			}),
			ad: new FormGroup({
				ad: new FormControl(),
				comment: new FormControl()
			}),
			interest: new FormGroup({
				interest: new FormControl(),
				comment: new FormControl()
			}),
			hisEmergency: new FormGroup({
				name: new FormControl('', Validators.required),
				phone: new FormControl('', Validators.required)
			}),
			herEmergency: new FormGroup({
				name: new FormControl('', Validators.required),
				phone: new FormControl('', Validators.required)
			}),
			comments: new FormControl()
		});
	}

	public updateForm(application: Application): void {
		this.form.controls['meetup'].setValue(application.meetup);
		this.form.controls['he'].patchValue(application.he);
		this.form.controls['she'].patchValue(application.she);
		this.form.controls['address'].patchValue(application.address);

		if (application.couple.catholicMarriageDate !== null && application.couple.catholicMarriageDate !== undefined) {
			const c = application.couple.catholicMarriageDate.split('-');
			application.couple.catholicDate = new Date(+c[0], +c[1], +c[2]);
		}	else {
			application.couple.catholicDate = new Date();
		}

		if (application.couple.legalMarriageDate !== null && application.couple.legalMarriageDate !== undefined) {
			const c = application.couple.legalMarriageDate.split('-');
			application.couple.legalDate = new Date(+c[0], +c[1], +c[2]);
		}	else {
			application.couple.legalDate = new Date();
		}

		this.form.controls['couple'].patchValue(application.couple);
		this.form.controls['ad'].patchValue(application.ad);
		this.form.controls['interest'].patchValue(application.interest);
		this.form.controls['hisEmergency'].patchValue(application.hisEmergency);
		this.form.controls['herEmergency'].patchValue(application.herEmergency);
		this.form.controls['comments'].setValue(application.comments);
	}

	public submitForm(): void {
		this.subscriptionManager.add(this.meetups.map(ms => ms.filter(m => m.$key === this.form.value.meetup)[0]).subscribe(next => {
			const vm: Application = this.form.value;

			if (vm.couple.catholicDate != null) {
				const d: Date = vm.couple.catholicDate;
				const day = d.getDate() >= 10 ? d.getDate() : '0' + d.getDate();

				vm.couple.catholicMarriageDate = d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + day;
			}

			if (vm.couple.legalDate != null) {
				const d: Date = vm.couple.legalDate;
				const day = d.getDate() >= 10 ? d.getDate() : '0' + d.getDate();

				vm.couple.legalMarriageDate = d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + day;
			}

			if (vm.comments == null) {
				vm.comments = '';
			}

			delete vm.couple.catholicDate;
			delete vm.couple.legalDate;

			vm.meetupDescription = next.description;

			this.applicationService.updateApplication(this.key, vm);

			this.snackBar.open('Solicitud Actualizada!', '', {
				duration: 2000,
			});
		}));
	}

	public removePayment(key: string): void {
		const dialogRef = this.dialog.open(RemovePaymentComponent, {
			width: '300px'
		});

		this.subscriptionManager.add(dialogRef.afterClosed().subscribe(next => {
			if (next === 'Yes') {
				this.applicationService.removePayment(key);
			}
		}));
	}
}



// WEBPACK FOOTER //
// C:/Source/Ecim/Ecim.Web/src/app/components/update-application/update-application.component.ts